import { queryOptions, useQuery } from '@tanstack/react-query';

import type { GetResponse } from '@tf/api';
import { gql, GraphQLClient } from '@tf/api';

import { getObjectUriByNodeId } from '@/core/api/legacy';
import type { ObjectType } from '@/core/api/utils/typeUtils';
import type { CaseRequestStatus, CaseStatus } from '@/core/types/cases';

export type ProfileCreatedEvent = ObjectType<'profile_created'>;

export type ProfileStatusChangedEvent = ObjectType<
	'profile_status_changed',
	{ profile_status: 'PROSPECT' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED' | 'CLOSED' | 'ARCHIVED' }
>;

export type ProfileOnboardingDateChangedEvent = ObjectType<
	'profile_onboarding_date_changed',
	{ new_onboarding_date: string }
>;

export type OSReviewStateChangedEvent = ObjectType<
	'os_review_state_changed',
	{
		os_review_state_value: 'DRAFT' | 'READY_TO_REVIEW' | 'UNDER_REVIEW' | 'UPDATE_REQUIRED' | 'VERIFIED';
	}
> & {
	nestedRelation: {
		object: {
			meta: {
				review_relation_kind: string;
			};
		};
	}[];
};

export type ProfileConnectionStatusChanged = ObjectType<
	'profile_connection_status_changed',
	{
		new_status: 'ACTIVE' | 'REMOVED';
	}
> & {
	nestedRelation: {
		object: {
			kind: string;
		};
	}[];
};

export type OSReviewCommentCreatedEvent = ObjectType<'os_review_comment_created'>;

export type ProfileAssignedManagersChanged = ObjectType<
	'profile_assigned_managers_changed',
	{
		new_managers?: string[];
		removed_managers?: string[];
	}
>;

export type ProfileCRACalculatedEvent = ObjectType<'profile_cra_calculated', { risk_level: string }>;

export type ProfileKYCDateReachedEvent = ObjectType<'profile_kyc_date_reached'>;

export type CaseStatusChanged = ObjectType<
	'case_status_changed',
	{
		case_status: CaseStatus;
	}
>;

export type CaseRequestStatusChanged = ObjectType<
	'case_request_status_changed',
	{
		case_request_status: CaseRequestStatus;
	}
>;

export type CaseRequestViewed = ObjectType<'case_request_viewed'>;

export type ProfileEvent =
	| ProfileCreatedEvent
	| ProfileStatusChangedEvent
	| ProfileOnboardingDateChangedEvent
	| OSReviewStateChangedEvent
	| OSReviewCommentCreatedEvent
	| ProfileAssignedManagersChanged
	| ProfileCRACalculatedEvent
	| ProfileKYCDateReachedEvent
	| CaseStatusChanged
	| CaseRequestStatusChanged
	| CaseRequestViewed;

export const accountEventLogsQueryOptions = (accountId: number) => {
	return queryOptions({
		queryKey: ['accountEventsLog', accountId],
		queryFn: async () => {
			const profileObjectId = await getObjectUriByNodeId(accountId);

			const res = await GraphQLClient.getInstance().request<
				GetResponse<ProfileEvent>,
				{ profileId: string }
			>(
				gql`
					query ProfileEvents($profileId: JSON!) {
						get(
							rootFilter: {
								archetype: "event"
								meta: {
									AND: [
										{
											OR: [
												{ key: "/@with/profile", operator: EQ, value: $profileId }
												{ key: "/@with/profile_a", operator: EQ, value: $profileId }
												{ key: "/@with/profile_b", operator: EQ, value: $profileId }
											]
										}
										{ key: "/@with/payment", operator: EQ, value: null }
									]
								}
							}
							paginationFilter: {
								limit: 100
								offset: 0
								sortByMeta: "/@createdAt"
								sortByDirection: DESC
							}
						) {
							id
							kind
							meta
							data
							nestedRelation(
								filter: {
									OR: [
										{ relationKind: "os_review_state" }
										{ relationKind: "profile_a" }
										{ relationKind: "profile_b" }
									]
								}
							) {
								object {
									kind
									meta
								}
							}
						}
					}
				`,
				{ profileId: profileObjectId }
			);

			return res.get;
		},
	});
};

export const useAccountEventsLog = (accountId: number) => {
	return useQuery(accountEventLogsQueryOptions(accountId));
};
